import { Link } from "react-router-dom";
import { PenLine, Quote, ArrowUpRight } from "lucide-react";

/*
  PERSONAL STORY PAGE — written by the author, lightly edited for flow.
  To update later, just edit the `paragraphs` and `pullQuote` strings inside
  the `sections` array below.
*/

const sections = [
  {
    id: "first-seizure",
    label: "01",
    heading: "Chapter One — First seizure",
    paragraphs: [
      "I was thirteen, in seventh grade, and living in Hong Kong when I had my first seizure. I was at school, and I had just won a piggyback race — two people on my back — when I started to shake. I didn't think anything of it. But when it carried on into my next lesson, I was sent to the nurse, and then sent home.",
      "That night, when it happened again, I was taken to the hospital. And that's where the tests began.",
    ],
  },
  {
    id: "the-tests",
    label: "02",
    heading: "Chapter Two — Hospitals",
    paragraphs: [
      "There were blood tests every day — which, for someone terrified of needles, was not ideal. There were MRIs, heart scans, brain scans, more than once. There was poking and prodding, and not much in the way of answers.",
      "When the hospital kept finding nothing and kept testing anyway, my mum decided enough was enough. She brought me home and started looking for specialists in Australia.",
    ],
  },
  {
    id: "in-between",
    label: "03",
    heading: "Chapter Three — Trying to find help",
    paragraphs: [
      "While we waited to get help in Australia and tried to manage things in Hong Kong, I attempted to go to school every day. But it wasn't safe for me to be out on my own, and the school didn't really know what to do with me.",
      "Eventually, Mum decided she and I would fly to Perth to look for answers there. So off we went — leaving my younger sisters and my dad behind — chasing a diagnosis.",
    ],
  },
  {
    id: "perth-no-help",
    label: "04",
    heading: "Chapter Four — Moving to Perth",
    paragraphs: [
      "Spoiler: the hospitals in Perth didn't help either. Instead, they told me there was nothing they could do, and they sent me home.",
      "That's the thing about FND. Nobody knows how to help. And almost nobody wants to be the one to diagnose it.",
    ],
  },
  {
    id: "finding-vance",
    label: "05",
    heading: "Chapter Five — Getting help",
    paragraphs: [
      "After all the begging and being turned away, my mum went online and tried to find someone — anyone — who actually understood. And, surprisingly, she did.",
      "That's how I got in with Vance Locke, an FND specialist who helped me get diagnosed and start learning how to live with it. To this day, I still have seizures. But they're becoming less and less frequent.",
    ],
  },
  {
    id: "the-move",
    label: "06",
    heading: "Chapter Six — The results",
    paragraphs: [
      "Once my seizures calmed down a little, we went back to Hong Kong, and I tried school again. But the seizures were still too unpredictable, and I still couldn't really go.",
      "That's when my parents made the call to move us to Perth, so I could attend an alternative school and get the support I actually needed. It meant leaving my dad behind — he had to stay for work — and uprooting my sisters, who came with us.",
      "Naturally, it caused fights. Lots of them. And blame.",
      "But it wasn't my fault. And it's not yours either.",
    ],
  },
];

export default function PersonalStory() {
  return (
    <div data-testid="page-personal-story">
      {/* Header */}
      <section className="pt-16 lg:pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-8">
            <span className="w-6 h-px bg-terracotta" />
            A personal account
          </span>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <h1
              data-testid="story-title"
              className="lg:col-span-8 font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl lg:text-7xl leading-[1.03] tracking-tight text-balance"
            >
              My story, in
              <span className="italic text-terracotta"> my own </span>
              words.
            </h1>
            <p className="lg:col-span-4 text-lg text-charcoal-light dark:text-bone/70 leading-relaxed">
              What happened, in the order it happened. From my first seizure in
              Hong Kong at thirteen, to the move to Perth that finally got me
              a diagnosis.
            </p>
          </div>
        </div>
      </section>

      {/* Author note */}
      <section className="pb-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div
            data-testid="story-author-note"
            className="flex flex-col md:flex-row gap-5 md:items-center rounded-3xl border border-moss/30 bg-moss/10 dark:bg-moss/15 p-6 lg:p-8"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-moss/20 text-moss shrink-0">
              <PenLine size={20} strokeWidth={1.5} />
            </span>
            <p className="text-base text-charcoal dark:text-bone leading-relaxed">
              <strong className="font-semibold">A note from me.</strong>{" "}
              These are my own words. If any of it sounds familiar — the
              dismissals, the tests that found nothing, the years spent
              looking for someone who’d listen — I’m sorry. You’re not alone.
            </p>
          </div>
        </div>
      </section>

      {/* Chapters */}
      <section className="pb-10" data-testid="story-chapters">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          {sections.map((s) => (
            <article
              key={s.id}
              id={s.id}
              data-testid={`story-chapter-${s.id}`}
              className="py-16 border-b border-bone-200 dark:border-charcoal-light/20 last:border-0"
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
                <div className="md:col-span-3">
                  <span className="text-xs uppercase tracking-[0.28em] text-terracotta">
                    {s.label}
                  </span>
                </div>
                <div className="md:col-span-9 space-y-6">
                  <h2 className="font-serif text-charcoal dark:text-bone text-3xl sm:text-4xl leading-[1.1] tracking-tight text-balance">
                    {s.heading}
                  </h2>
                  {s.paragraphs.map((p, idx) => (
                    <p
                      key={idx}
                      className="text-lg text-charcoal-light dark:text-bone/75 leading-relaxed"
                    >
                      {p}
                    </p>
                  ))}
                  {s.pullQuote && (
                    <blockquote className="mt-6 pl-6 border-l-2 border-terracotta/60">
                      <div className="flex items-start gap-3">
                        <Quote size={18} strokeWidth={1.5} className="text-terracotta mt-1 shrink-0" />
                        <p className="font-serif italic text-2xl text-charcoal dark:text-bone leading-snug">
                          {s.pullQuote}
                        </p>
                      </div>
                    </blockquote>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Closing line */}
      <section className="pb-16" data-testid="story-closing">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <p className="font-serif italic text-2xl sm:text-3xl text-charcoal dark:text-bone leading-snug text-balance">
            “It wasn’t my fault. And it’s not yours either.”
          </p>
        </div>
      </section>

      {/* Soft support prompt */}
      <section className="pb-16" data-testid="story-support-prompt">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone-100/40 dark:bg-charcoal-light/10 p-7 lg:p-9 flex flex-col sm:flex-row sm:items-center gap-5">
            <p className="text-base text-charcoal-light dark:text-bone/75 leading-relaxed flex-1">
              If any of this has helped and you’re in Australia, you can send a
              quiet PayID transfer. Or just share the site — that helps too.
            </p>
            <Link
              to="/support"
              data-testid="story-support-link"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-terracotta/40 text-terracotta hover:bg-terracotta hover:text-bone transition text-sm font-medium shrink-0"
            >
              Support this site
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Link
            to="/resources"
            data-testid="story-cta-resources"
            className="group relative block overflow-hidden rounded-[2rem] bg-charcoal dark:bg-bone-100/10 text-bone p-10 lg:p-14 hover-lift border border-transparent dark:border-bone-200/10"
          >
            <span className="text-xs uppercase tracking-[0.28em] text-bone/60">If you need more</span>
            <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Resources & support — gathered with care.
            </h3>
            <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium border-b border-bone/40 pb-1 group-hover:border-terracotta group-hover:text-terracotta transition">
              Open resources
              <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
            <div className="pointer-events-none absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-terracotta/20 blur-3xl" />
          </Link>
        </div>
      </section>
    </div>
  );
}
